import { useEffect, useRef, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ColumnDef<T> {
    key: keyof T | string;
    header: string;
    sortable?: boolean;
    sortValue?: (row: T) => string | number;
    serverSortKey?: string;
    /** Flip the direction sent to the server (for derived columns whose order is the inverse of their backing field, e.g. DaysWaiting vs Created). */
    serverSortInvert?: boolean;
    sticky?: boolean;
    render?: (row: T) => React.ReactNode;
    /** Extra classes for this column's body cell on a given row (e.g. a per-cell highlight). */
    cellClassName?: (row: T) => string | undefined;
}

export interface ServerSideOptions {
    onParamsChange: (
        sort: { key: string; direction: 'asc' | 'desc' } | null,
        page: { skip: number; top: number }
    ) => void;
    total?: number;
    pageSize?: number;
}

interface DataTableProps<T> {
    data: T[];
    columns: ColumnDef<T>[];
    loading?: boolean;
    error?: string;
    className?: string;
    rowClassName?: (row: T) => string | undefined;
    serverSide?: ServerSideOptions;
    /** Page the rows in the browser (ignored with serverSide). Sorting still covers every row. */
    pageSize?: number;
}

type SortDirection = 'asc' | 'desc' | null;

const SortIcon = ({ direction }: { direction: SortDirection }) => {
    if (direction === 'asc') return <ChevronUp className="h-4 w-4" />;
    if (direction === 'desc') return <ChevronDown className="h-4 w-4" />;
    return <ChevronsUpDown className="h-4 w-4 text-muted-foreground/50" />;
};

const getValue = <T,>(row: T, key: keyof T | string): unknown =>
    key.toString().split('.').reduce((obj: any, k) => obj?.[k], row);

export function DataTable<T>({ data = [], columns, loading, error, className, rowClassName, serverSide, pageSize: clientPageSize }: DataTableProps<T>) {
    const [sortKey, setSortKey] = useState<keyof T | string | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);
    const [skip, setSkip] = useState(0);
    const [pageInput, setPageInput] = useState('1');
    const [stickyOffsets, setStickyOffsets] = useState<Record<number, number>>({});
    const headerRefs = useRef<(HTMLTableCellElement | null)[]>([]);
    const scrollRef = useRef<HTMLDivElement>(null);
    const isMobile = window.innerWidth <= 768;

    const clientPaged = !serverSide && clientPageSize != null;
    const pageSize = serverSide?.pageSize ?? clientPageSize ?? 50;
    const total = serverSide ? (serverSide.total ?? 0) : data.length;
    const totalPages = Math.ceil(total / pageSize);
    const currentPage = Math.floor(skip / pageSize) + 1;

    // New rows (e.g. a filter change) start again from the first page.
    useEffect(() => {
        if (clientPaged) setSkip(0);
    }, [data, clientPaged]);

    // A new page or sort order starts at its first row, not wherever the last one was scrolled to.
    useEffect(() => {
        if (clientPaged) scrollRef.current?.scrollTo?.({ top: 0 });
    }, [skip, sortKey, sortDirection, data, clientPaged]);

    useEffect(() => {
        const offsets: Record<number, number> = {};
        let cumulative = 0;
        columns.forEach((col, i) => {
            if (col.sticky) {
                offsets[i] = cumulative;
                cumulative += headerRefs.current[i]?.offsetWidth ?? 0;
            }
        });
        setStickyOffsets(offsets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.length]);

    const resolveServerSort = (key: keyof T | string, direction: 'asc' | 'desc'): { key: string; direction: 'asc' | 'desc' } => {
        const col = columns.find(c => c.key === key);
        return {
            key: (col?.serverSortKey ?? key).toString(),
            direction: col?.serverSortInvert ? (direction === 'asc' ? 'desc' : 'asc') : direction,
        };
    };

    const handleSort = (key: keyof T | string) => {
        let newDirection: SortDirection;
        let newKey: keyof T | string | null;

        if (sortKey !== key) {
            newKey = key;
            newDirection = 'asc';
        } else if (sortDirection === 'asc') {
            newKey = key;
            newDirection = 'desc';
        } else {
            newKey = null;
            newDirection = null;
        }

        setSortKey(newKey);
        setSortDirection(newDirection);

        if (serverSide) {
            const newSkip = 0;
            setSkip(newSkip);
            serverSide.onParamsChange(
                newKey && newDirection ? resolveServerSort(newKey, newDirection) : null,
                { skip: newSkip, top: pageSize }
            );
        } else if (clientPaged) {
            setSkip(0);
        }
    };

    const handlePage = (newSkip: number) => {
        setSkip(newSkip);
        serverSide?.onParamsChange(
            sortKey && sortDirection ? resolveServerSort(sortKey, sortDirection) : null,
            { skip: newSkip, top: pageSize }
        );
    };

    // Keep the editable page field in sync with the current page (e.g. arrow buttons, sort resets).
    useEffect(() => {
        setPageInput(String(currentPage));
    }, [currentPage]);

    const commitPageInput = () => {
        const parsed = parseInt(pageInput, 10);
        if (Number.isNaN(parsed)) {
            setPageInput(String(currentPage));
            return;
        }
        const clamped = Math.min(Math.max(parsed, 1), totalPages);
        setPageInput(String(clamped));
        if (clamped !== currentPage) {
            handlePage((clamped - 1) * pageSize);
        }
    };

    const sorted = serverSide
        ? data
        : [...data].sort((a, b) => {
            if (!sortKey || !sortDirection) return 0;
            const col = columns.find(c => c.key === sortKey);
            const aVal = col?.sortValue ? col.sortValue(a) : getValue(a, sortKey);
            const bVal = col?.sortValue ? col.sortValue(b) : getValue(b, sortKey);
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            const result = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            return sortDirection === 'asc' ? result : -result;
        });

    const rows = clientPaged ? sorted.slice(skip, skip + pageSize) : sorted;

    const isSticky = (col: ColumnDef<T>) => col.sticky && !isMobile;

    const stickyStyle = (i: number) =>
        !isMobile && i in stickyOffsets ? { left: stickyOffsets[i] } : undefined;

    const stickyClass = 'sticky z-10 bg-background after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border';
    // Data-row sticky cells inherit the row's background so rowClassName highlights
    // (and hover tints) show through. Those tints can be translucent (hover:bg-muted/50), and
    // on their own would let the columns scrolled underneath bleed through. So the cell paints
    // an opaque background-image over its inherited color, and a ::before (inheriting the same
    // color, beneath the content in the cell's sticky stacking context) lays the tint back on top.
    const stickyCellClass = cn(
        'sticky z-10 bg-inherit bg-[linear-gradient(hsl(var(--background)),hsl(var(--background)))]',
        'before:absolute before:inset-0 before:-z-10 before:bg-inherit',
        'after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border'
    );

    return (
        // min-h-0 lets a height-bounded flex parent shrink the table, so its rows scroll inside it
        // instead of pushing the bottom pager off screen; elsewhere the 80vh cap still applies.
        <div className="flex min-h-0 flex-col gap-2">
            <div ref={scrollRef} className="overflow-auto max-h-[80vh] rounded-md border">
                <Table className={className}>
                    <TableHeader>
                        <TableRow>
                            {columns.map((col, i) => (
                                <TableHead
                                    key={col.key.toString()}
                                    ref={el => { headerRefs.current[i] = el; }}
                                    className={cn(
                                        'sticky top-0 bg-background shadow-[0_1px_0_0_hsl(var(--border))]',
                                        isSticky(col)
                                            ? 'z-30 after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border'
                                            : 'z-20'
                                    )}
                                    style={stickyStyle(i)}
                                >
                                    {col.sortable ? (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="-ml-3 h-8 gap-1 font-medium"
                                            onClick={() => handleSort(col.key)}
                                        >
                                            {col.header}
                                            <SortIcon direction={sortKey === col.key ? sortDirection : null} />
                                        </Button>
                                    ) : col.header}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {error ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center text-destructive text-sm">
                                    {error}
                                </TableCell>
                            </TableRow>
                        ) : loading ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={i}>
                                    {columns.map((col, j) => (
                                        <TableCell
                                            key={col.key.toString()}
                                            className={cn(isSticky(col) && stickyClass)}
                                            style={stickyStyle(j)}
                                        >
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                                    No results.
                                </TableCell>
                            </TableRow>
                        ) : rows.map((row, i) => (
                            <TableRow key={i} className={cn('bg-background', rowClassName?.(row))}>
                                {columns.map((col, j) => (
                                    <TableCell
                                        key={col.key.toString()}
                                        className={cn(isSticky(col) && stickyCellClass, col.cellClassName?.(row))}
                                        style={stickyStyle(j)}
                                    >
                                        {col.render ? col.render(row) : String(getValue(row, col.key) ?? '')}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            {(serverSide || clientPaged) && (
                <div className="flex items-center justify-end px-1 text-sm text-muted-foreground gap-3">
                    <span>
                        {total > 0
                            ? `Showing ${skip + 1}–${Math.min(skip + pageSize, total)} of ${total}`
                            : 'No results'}
                    </span>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePage(skip - pageSize)}
                            disabled={skip === 0 || loading}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        {totalPages > 2 ? (
                            <span className="flex items-center gap-1.5 px-2">
                                Page
                                <Input
                                    type="text"
                                    inputMode="numeric"
                                    aria-label="Go to page"
                                    value={pageInput}
                                    disabled={loading}
                                    onChange={e => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
                                    onFocus={e => e.target.select()}
                                    onBlur={commitPageInput}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            commitPageInput();
                                            e.currentTarget.blur();
                                        }
                                    }}
                                    className="h-8 w-12 text-center"
                                />
                                of {totalPages}
                            </span>
                        ) : (
                            <span className="px-2">Page {currentPage} of {totalPages}</span>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePage(skip + pageSize)}
                            disabled={skip + pageSize >= total || loading}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
